import { useCallback, useRef, useState, type ReactNode } from "react"
import { GripVertical, PanelLeftOpen } from "lucide-react"

import { MobileNav } from "@/components/layout/mobile-nav"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import { useResizableWidth } from "@/hooks/use-resizable-width"
import { cn } from "@/lib/utils"
import { ChatPane } from "./chat-pane"
import { ConversationSidebar, RAIL_PX } from "./conversation-sidebar"
import { useRobinWorkspace } from "./hooks/use-robin-workspace"

/**
 * The seam between the thread and the ATS: a floating pill straddling the edge,
 * not a bar.
 *
 * The canvas draws it 8x96, which is a picture rather than a target — 8px is
 * under half of what a pointer wants and the grip inside it had nowhere to sit.
 * It is 14x112 here, over a 16px grab strip the handle itself provides, and it
 * takes the group's own drag state so it lights while it is being moved.
 *
 * Both seams sit at z-60, above the candidate record and below anything modal.
 * The record is a z-50 overlay *inside* the ATS panel — canvas 6a opens it over
 * that column only, so the thread and the rail stay beside it — and at z-4 the
 * pill was painted over from the seam rightwards, leaving half a pill on screen.
 * Dialogs and sheets portal to the body at z-100 and cover the seams, because a
 * handle showing through a modal claims to be usable when it is not. The scale
 * is written out in `index.css`.
 *
 * The rail's seam has no pill at all. A column with a visible edge between it
 * and the next one is already understood to be draggable, and a second pill six
 * inches from the first only asks which of them does something different.
 */
function SeamPill() {
  return (
    <div className="flex h-28 w-3.5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors group-hover:border-primary group-hover:text-brand-ink group-data-[resize-handle-state=drag]:border-primary group-data-[resize-handle-state=drag]:text-brand-ink">
      <GripVertical className="h-4 w-4" />
    </div>
  )
}

/*
  The thread's floor, as a share of the group. It is a percentage because the
  group is; what matters is that it exists, and that pushing past it now spends
  the rail rather than doing nothing.
*/
const CHAT_MIN_PERCENT = 24

export function RobinWorkspace({ children }: { children: ReactNode }) {
  const workspace = useRobinWorkspace()
  const isMobile = useIsMobile()
  const [robinSheetOpen, setRobinSheetOpen] = useState(false)

  /*
    The rail is sized in pixels rather than as a share of the group, because what
    it holds is fixed-size: two lines of 12.5px text and a timestamp. `maxShare`
    is the one proportional part — a width chosen on a wide display should not
    survive intact onto a laptop.
  */
  const rail = useResizableWidth({
    storageKey: "robin:rail-width",
    initial: RAIL_PX.canvas,
    min: RAIL_PX.min,
    max: RAIL_PX.max,
    maxShare: 0.24,
    collapseAt: RAIL_PX.collapseAt,
  })

  /*
    The thread has a floor and the rail did not, which made the protection
    one-sided: pushing the thread closed simply stopped, while the rail could be
    squeezed to nothing by the window. Now, when the reader drags the thread
    down onto its floor and keeps pushing, the rail is what gives — the thread
    keeps a width it can be read at, and the space comes from the column that
    was only a list of names.
  */
  const draggingChatSeam = useRef(false)
  const onLayout = useCallback(
    (sizes: number[]) => {
      const chat = sizes[0]
      if (
        draggingChatSeam.current &&
        !rail.collapsed &&
        chat !== undefined &&
        chat <= CHAT_MIN_PERCENT + 0.5
      ) {
        rail.toggle()
      }
    },
    [rail],
  )

  const chatPane = (
    <ChatPane
      conv={workspace.selected}
      actions={workspace.actions}
      onOpenSidebar={() => workspace.setMobileSidebarOpen(true)}
    />
  )

  const conversationSidebar = (
    <ConversationSidebar
      conversations={workspace.conversations}
      selectedId={workspace.selected.id}
      onSelect={(id) => {
        workspace.handleSelectConversation(id)
        setRobinSheetOpen(true)
      }}
      onNewChat={() => {
        workspace.handleNewChat()
        setRobinSheetOpen(true)
      }}
      onCollapse={isMobile ? undefined : rail.toggle}
    />
  )

  /*
    Below the mobile breakpoint there is no room for a split, so the panel group
    is not merely hidden — it is not mounted. The phone shows the ATS full width
    with the bottom nav from artboard 12b, and Robin becomes a destination rather
    than a pane: its tab opens the thread over the top.

    This is a structural switch rather than a CSS one because two mounted panel
    groups would fight over the same `autoSaveId`.
  */
  if (isMobile) {
    return (
      <div className="flex h-dvh w-full flex-col overflow-hidden bg-background text-foreground">
        <div className="min-h-0 flex-1 overflow-hidden">{children}</div>

        <MobileNav onOpenRobin={() => setRobinSheetOpen(true)} />

        <Sheet open={robinSheetOpen} onOpenChange={setRobinSheetOpen}>
          <SheetContent side="bottom" className="h-[92dvh] p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Robin</SheetTitle>
              <SheetDescription>Your conversation with the assistant</SheetDescription>
            </SheetHeader>
            <div className="h-full">{chatPane}</div>
          </SheetContent>
        </Sheet>

        <Sheet
          open={workspace.mobileSidebarOpen}
          onOpenChange={workspace.setMobileSidebarOpen}
        >
          {/*
            There is nothing to drag on a phone, so the rail opens at the canvas
            width — never wider than the phone it opens on.
          */}
          <SheetContent
            side="left"
            className="p-0"
            style={{ width: `min(${RAIL_PX.canvas}px, 82vw)` }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Conversations</SheetTitle>
              <SheetDescription>
                Your conversations with the assistant
              </SheetDescription>
            </SheetHeader>
            {conversationSidebar}
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-background text-foreground">
      {/* Chrome, per the canvas: the conversation rail is the frame, darker
          than the page it sits beside. */}
      <aside
        style={{ width: rail.width }}
        className="shrink-0 overflow-hidden bg-chrome"
      >
        {conversationSidebar}
      </aside>

      {/*
        The rail was the one column a reader could not size, and it was the one
        holding their own history. It moves pixels rather than percentages — see
        `useResizableWidth` for why — and it carries no pill: the edge between
        two columns is affordance enough, and a second pill beside the group's
        own only invites the question of what the two do differently. The grab
        strip is 14px of nothing over the hairline, which lights while held.
      */}
      <div
        {...rail.handleProps}
        aria-label="Resize the conversation list"
        onDoubleClick={rail.toggle}
        className={cn(
          "relative z-[60] w-px flex-none cursor-col-resize border-r bg-transparent outline-none transition-colors",
          "before:absolute before:inset-y-0 before:-left-1.5 before:w-3.5 before:content-['']",
          "hover:border-primary focus-visible:border-primary",
          rail.dragging && "border-primary",
        )}
      >
        {/*
          A closed column has to say how to open it, or it has been deleted as
          far as the reader is concerned. The seam still drags, and a
          double-click on it toggles either way.
        */}
        {rail.collapsed && (
          <button
            type="button"
            /* The seam captures the pointer to drag, which would swallow this
               click before it ever became one. */
            onPointerDown={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            onClick={rail.toggle}
            aria-label="Show the conversation list"
            title="Show the conversation list"
            className="absolute left-1.5 top-3 z-[61] flex size-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <PanelLeftOpen className="size-[15px]" />
          </button>
        )}
      </div>

      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="robin-workspace"
        onLayout={onLayout}
        className="min-w-0 flex-1"
      >
        {/*
          ATS-dominant but not marginal: the chat pane has to be wide enough that
          Robin's answer is readable on arrival, or the differentiator is a sliver
          nobody drags open. `autoSaveId` persists the user's own ratio after that,
          so these only apply on first run.
        */}
        <ResizablePanel id="chat" order={1} defaultSize={35} minSize={CHAT_MIN_PERCENT}>
          {/* Per the canvas: the chat pane sits above the ATS — rounded right
              edge, hairline, and a soft shadow cast rightwards. */}
          <div className="relative z-[3] h-full rounded-r-lg border-r shadow-[10px_0_26px_-16px_rgba(0,0,0,0.95)]">
            {chatPane}
          </div>
        </ResizablePanel>

        {/* `after:` is the handle's own hit area, a 4px sliver by default. */}
        <ResizableHandle
          aria-label="Resize the thread"
          onDragging={(active) => {
            draggingChatSeam.current = active
          }}
          className="group z-[60] w-px overflow-visible bg-transparent after:w-4"
        >
          <SeamPill />
        </ResizableHandle>

        <ResizablePanel id="ats" order={2} defaultSize={65} minSize={42}>
          {/* `relative` makes this the positioning context for record overlays.
              Canvas 6a opens the candidate over the ATS column only — the rail
              and the thread stay visible beside it — so those overlays are
              absolute within this panel rather than fixed to the viewport. */}
          <div className="relative h-full min-w-0 overflow-hidden bg-background">
            {children}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}
